import { fetchAuthSession } from 'aws-amplify/auth';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

const getHeaders = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    return {
      'Authorization': `Bearer ${tokens.idToken.toString()}`,
      'Content-Type': 'application/json'
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    throw error;
  }
};

export const apiService = {
  // Image management
  async getImages() {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/images`, {
      method: 'GET',
      headers
    });
    if (!response.ok) throw new Error('Failed to fetch images');
    return response.json();
  },
  async uploadImage(file, newFileName) {
    if (!file.type.match(/^image\/(jpe?g)$/i)) {
      throw new Error('Only JPG/JPEG files are allowed');
    }

    const headers = await getHeaders();
    delete headers['Content-Type']; // Let the browser set the correct content type for the form data

    const formData = new FormData();
    // If a new filename is provided, create a new blob with the desired name
    let finalFileName;
    if (newFileName) {
      const extension = file.name.split('.').pop().toLowerCase();
      finalFileName = `${newFileName}.${extension}`;
      const renamedFile = new File([file], finalFileName, {
        type: file.type
      });
      formData.append('file', renamedFile);
    } else {
      finalFileName = file.name;
      formData.append('file', file);
    }

    // Add the file name to headers
    headers['x-file-name'] = finalFileName;

    const response = await fetch(`${API_ENDPOINT}/images/upload`, {
      method: 'POST',
      headers,
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload image');
    }
    return response.json();
  },
  async deleteImage(imageName) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/images/${imageName}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) throw new Error('Failed to delete image');
    return { success: true };
  },
  // Schedule management
  async getSchedule(classLink, scheduleId) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/schedule/${classLink}/${scheduleId}`, {
      method: 'GET',
      headers
    });
    if (!response.ok) throw new Error('Failed to fetch events');
    return response.json();
  },
  async updateSchedule(classLink, scheduleId, formData) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/schedule/${classLink}/${scheduleId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(formData)
    });
    if (!response.ok) throw new Error('Failed to update event');
    return { success: true };
  },
  async createSchedule(classLink, scheduleId, formData) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/schedule/${classLink}/${scheduleId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(formData)
    });
    if (!response.ok) throw new Error('Failed to create schedule');
    return response.json();
  },
  async deleteSchedule(classLink, scheduleId) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/schedule/${classLink}/${scheduleId}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) throw new Error('Failed to delete schedule');
    return { success: true };
  },

  // Lesson management
  async getLesson(name, link) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/lesson/${name}/${link}`, {
      method: 'GET',
      headers
    });
    if (!response.ok) throw new Error('Failed to fetch lesson');
    return response.json();
  },
  async updateLesson(name, link, formData) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/lesson/${name}/${link}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(formData)
    });
    if (!response.ok) throw new Error('Failed to update lesson');
    return response.json();
  },
  async createLesson(name, link, formData) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/lesson/${name}/${link}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(formData)
    });
    if (!response.ok) throw new Error('Failed to create lesson');
    return response.json();
  },
  async deleteLesson(name, link) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/lesson/${name}/${link}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) throw new Error('Failed to delete lesson');
    return { success: true };
  },

  // Event management
  async getEvent(name, link) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/event/${name}/${link}`, {
      method: 'GET',
      headers
    });
    if (!response.ok) throw new Error('Failed to fetch event');
    return response.json();
  },
  async updateEvent(name, link, formData) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/event/${name}/${link}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(formData)
    });
    if (!response.ok) throw new Error('Failed to update event');
    return response.json();
  },
  async createEvent(name, link, formData) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/event/${name}/${link}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(formData)
    });
    if (!response.ok) throw new Error('Failed to create event');
    return response.json();
  },
  async deleteEvent(name, link) {
    const headers = await getHeaders();
    const response = await fetch(`${API_ENDPOINT}/event/${name}/${link}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) throw new Error('Failed to delete event');
    return { success: true };
  }
};

